import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import * as api from "../api/client";
import DataTable from "../components/DataTable";
import TableDescriptionModal from "../components/TableDescriptionModal";
import ColumnDescriptionsModal from "../components/ColumnDescriptionsModal";

function fmtDate(epochSeconds) {
  return epochSeconds ? new Date(epochSeconds * 1000).toLocaleString() : "—";
}

// Admin-only. The semantic layer: describe what each dataset table means so the Data agent's
// list_fields tool can hand that description to the model, instead of the agent (or a user) having to
// guess from column names alone. Same DataTable+modal pattern as Users/Roles.
export default function TableCatalogPage() {
  const [descriptions, setDescriptions] = useState([]);
  const [dbTables, setDbTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [modal, setModal] = useState(null); // { mode: "create" | "edit", item? }
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState("");
  const [columnsFor, setColumnsFor] = useState(null); // table name, or null

  const [columnCounts, setColumnCounts] = useState({}); // { table: how many columns are described }

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [d, t, c] = await Promise.all([
        api.listTableDescriptions(),
        api.listDbTables(),
        api.listColumnDescriptions(),
      ]);
      setDescriptions(d.descriptions || []);
      setDbTables(t.tables || []);
      const counts = {};
      for (const row of c.descriptions || []) counts[row.tableName] = (counts[row.tableName] || 0) + 1;
      setColumnCounts(counts);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function closeModal() {
    setModal(null);
    setModalError("");
  }

  async function handleSubmit(payload) {
    setSaving(true);
    setModalError("");
    try {
      if (modal.mode === "edit") {
        await api.updateTableDescription(modal.item.id, payload.description);
      } else {
        await api.createTableDescription(payload.tableName, payload.description);
      }
      closeModal();
      await load();
    } catch (e) {
      setModalError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function removeDescription(id, tableName) {
    if (!window.confirm(`Remove the description for "${tableName}"?`)) return;
    setError("");
    try {
      await api.deleteTableDescription(id);
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  const describedTables = new Set(descriptions.map((d) => d.tableName));
  const undescribedTables = dbTables.filter((t) => !describedTables.has(t));

  // Every dataset table gets a row, described or not. Listing only described ones made the page read
  // as empty whenever table_metadata was — and since "Manage columns" lives on a row, an empty list
  // also left the per-column descriptions (a separate store, populated in bulk by
  // `python -m app.load_descriptions`) with no way to reach them at all. Showing the full inventory
  // also makes "what have I not described yet?" answerable, which is the page's actual job.
  const byTable = new Map(descriptions.map((d) => [d.tableName, d]));
  const rows = dbTables.map((t) => ({
    ...(byTable.get(t) || { id: null, tableName: t, description: "", createdAt: null }),
    columnCount: columnCounts[t] || 0,
    rowKey: `table:${t}`,
  }));

  const columns = [
    { key: "tableName", label: "Table", sortable: true, width: 200 },
    {
      key: "description",
      label: "Description",
      render: (d) =>
        d.description ? (
          <span title={d.description} style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {d.description}
          </span>
        ) : (
          <span style={{ color: "var(--muted)" }}>— not described —</span>
        ),
    },
    {
      key: "columnCount",
      label: "Columns described",
      sortable: true,
      width: 160,
      render: (d) =>
        d.columnCount ? (
          <span className="pill neutral">{d.columnCount}</span>
        ) : (
          <span style={{ color: "var(--muted)" }}>none</span>
        ),
    },
    { key: "createdAt", label: "Created", sortable: true, width: 180, render: (d) => fmtDate(d.createdAt) },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      width: 120,
      render: (d) => (
        <div className="dt-actions">
          <button className="dt-icon-btn" title="Manage columns" onClick={() => setColumnsFor(d.tableName)}>
            ▤
          </button>
          <button
            className="dt-icon-btn"
            title={d.id ? "Edit description" : "Describe this table"}
            onClick={() => setModal(d.id ? { mode: "edit", item: d } : { mode: "create", table: d.tableName })}
          >
            {d.id ? "✎" : "+"}
          </button>
          {d.id && (
            <button className="dt-icon-btn danger" title="Delete" onClick={() => removeDescription(d.id, d.tableName)}>
              🗑
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <main className="main">
      <div className="topbar">
        <h1>Table Catalog</h1>
        <p>
          Describe what each dataset table means, and what its individual columns mean (▤). The agent
          reads both on every query. Table descriptions also steer which agent a question is routed to.
        </p>
      </div>
      <div className="page-body">
        {error && (
          <Alert variant="danger" onClose={() => setError("")} dismissible>
            {error}
          </Alert>
        )}

        <div className="page-head-row">
          <Button variant="success" onClick={() => setModal({ mode: "create" })} disabled={loading || undescribedTables.length === 0}>
            + Describe a table
          </Button>
        </div>

        {loading ? (
          <Spinner animation="border" />
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            searchKeys={["tableName", "description"]}
            searchPlaceholder="Search tables..."
            emptyMessage="No dataset tables found — load one with `python -m app.load_dataset`."
          />
        )}

        {modal && (
          <TableDescriptionModal
            mode={modal.mode}
            initial={modal.item}
            // Describing from a row preselects that table; the toolbar button still offers the full
            // list of tables that have no description yet.
            availableTables={modal.table ? [modal.table] : undescribedTables}
            saving={saving}
            error={modalError}
            onClose={closeModal}
            onSubmit={handleSubmit}
          />
        )}

        {columnsFor && <ColumnDescriptionsModal tableName={columnsFor} onClose={() => setColumnsFor(null)} />}
      </div>
    </main>
  );
}
